import { useEffect, useRef, useState, type FormEvent } from "react";
import { SyntaxCode } from "@/office/SyntaxCode";
import { CloudPanel } from "@/system/CloudPanel";
import { clearCloudToken, cloudLogin, readCloudBase, readCloudToken, saveCloudBase } from "@/system/cloud";
import { resolveCloud, resolveLocal, type LocateQuality } from "@/system/locate";
import { SYSTEM_MODULES, findModule, readSource, type SourceDoc, type SystemModule } from "@/system/modules";

type MirrorStep = {
  id: number;
  moduleId: string;
  action: string;
  target: string;
  time: string;
  docId: string | null;
  line: number | null;
  /** 定位可信度：原文命中 / 按表头 / 表格兜底 / 没找到 */
  quality: LocateQuality;
  /** 为什么定位到这里（给用户看的一句话） */
  basis: string;
};

function qualityLabel(quality?: LocateQuality): string {
  if (quality === "exact") return "原文命中";
  if (quality === "header") return "按表头定位";
  if (quality === "fallback") return "表格兜底";
  return "未找到";
}

/** 取点击位置所在列的表头文字，作为数据类点击的定位锚点。 */
function headerOf(target: HTMLElement): string | null {
  const cell = target.closest("td") as HTMLTableCellElement | null;
  if (!cell || cell.nodeName !== "TD") return null;
  const table = cell.closest("table");
  if (!table) return null;
  const index = cell.cellIndex;
  if (index < 0) return null;
  const rows = [table.querySelector("thead tr"), table.querySelector("tr")].filter(
    (row): row is HTMLTableRowElement => Boolean(row),
  );
  for (const row of rows) {
    const heads = Array.from(row.children) as HTMLTableCellElement[];
    const text = (heads[index]?.innerText || "").replace(/\s+/g, "").trim();
    if (text.length >= 2 && text.length <= 12) return text;
  }
  return null;
}

function clock(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

function moduleUrl(mod: SystemModule, token: string, sameOrigin: boolean): string {
  // 同源时走本地反代（开发服 / 自建域名都算）：既能正常登录/操作，也能监听 iframe 内的点击。
  // 跨域 iframe 读不到 contentDocument，右侧就不会跟随点击，所以优先同源。
  // 子应用支持 ?t=<令牌> 注入登录态（官方嵌入方式），令牌写入各自 localStorage 后自动从地址栏移除。
  const base = mod.id === "main" || !sameOrigin ? mod.directUrl : mod.devPath;
  if (!token) return base;
  return `${base}${base.includes("?") ? "&" : "?"}t=${encodeURIComponent(token)}`;
}

const TOKEN_KEY = "jhzz_lingpai";

type LoginState = { zhanghao: string; mima: string };

export function OfficeMirror() {
  const [activeModule, setActiveModule] = useState("gongzi");
  const [docs, setDocs] = useState<{ doc: SourceDoc; code: string }[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  const [steps, setSteps] = useState<MirrorStep[]>([]);
  const [activeStepId, setActiveStepId] = useState<number | null>(null);
  const [frameKey, setFrameKey] = useState(0);
  const [recording, setRecording] = useState(true);
  const [pathLabel, setPathLabel] = useState("");
  const [ratio, setRatio] = useState(0.52);
  const [resizing, setResizing] = useState(false);
  const shell = useRef<HTMLDivElement>(null);
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) ?? "");
  const [userName, setUserName] = useState(() => sessionStorage.getItem("jhzz_xingming") ?? "");
  const [login, setLogin] = useState<LoginState>({ zhanghao: "", mima: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [sourceMode, setSourceMode] = useState<"local" | "cloud">(() =>
    localStorage.getItem("jhzz_source_mode") === "local" ? "local" : "cloud",
  );
  const [cloudRoot, setCloudRoot] = useState(() => (readCloudToken() ? sessionStorage.getItem("jhzz_code_root") ?? "已连接" : ""));
  const [cloudPassword, setCloudPassword] = useState("");
  const [cloudBase, setCloudBase] = useState(() => readCloudBase());
  const [cloudError, setCloudError] = useState("");
  const [cloudBusy, setCloudBusy] = useState(false);

  /** 本站是否反代了四个子应用（同源才能监听 iframe 内的点击） */
  const [sameOrigin, setSameOrigin] = useState(() => import.meta.env.DEV);

  const frameRef = useRef<HTMLIFrameElement>(null);
  const counter = useRef(0);
  const moduleRef = useRef(activeModule);
  const recordingRef = useRef(recording);
  const lastPath = useRef("");

  const docsRef = useRef<{ doc: SourceDoc; code: string }[]>([]);
  const cloudRef = useRef<{ mode: "local" | "cloud"; root: string }>({ mode: "local", root: "" });

  moduleRef.current = activeModule;
  recordingRef.current = recording;
  docsRef.current = docs;
  cloudRef.current = { mode: sourceMode, root: cloudRoot };

  // 切模块时把该模块的真实源文件读进来（只在第一次读，之后走缓存）
  useEffect(() => {
    let cancelled = false;
    setDocsLoading(true);
    setDocs([]);
    setActiveDocId(null);
    Promise.all(findModule(activeModule).sources.map(async (item) => ({ doc: item, code: await readSource(item) })))
      .then((loaded) => {
        if (cancelled) return;
        setDocs(loaded);
        setDocsLoading(false);
        setActiveDocId(findModule(activeModule).primary);
      })
      .catch(() => {
        if (!cancelled) setDocsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeModule]);

  // 非开发环境下探测一次：本站是否反代了子应用（反代会带 X-Mirror-Proxy 头）
  useEffect(() => {
    if (import.meta.env.DEV) return;
    let cancelled = false;
    fetch("/gongzibiao-app/", { cache: "no-store" })
      .then((response) => {
        if (!cancelled) setSameOrigin(response.headers.get("x-mirror-proxy") === "1");
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const mod = findModule(activeModule);
  const activeDoc = docs.find((item) => item.doc.id === activeDocId) ?? docs[0] ?? null;
  const activeStep = steps.find((item) => item.id === activeStepId) ?? null;
  const hitDoc = activeStep?.docId ? docs.find((item) => item.doc.id === activeStep.docId) ?? null : null;
  const highlight =
    activeStep && activeStep.line && activeDoc && activeStep.docId === activeDoc.doc.id
      ? { start: Math.max(1, activeStep.line - 1), end: activeStep.line + 1 }
      : undefined;

  const pushStep = (moduleId: string, action: string, target: string, anchor: string | null = null) => {
    if (!recordingRef.current) return;
    const current = findModule(moduleId);
    const onCloud = cloudRef.current.mode === "cloud" && Boolean(cloudRef.current.root);
    const local =
      !onCloud && moduleId === moduleRef.current ? resolveLocal(docsRef.current, moduleId, target, anchor) : null;

    counter.current += 1;
    const step: MirrorStep = {
      id: counter.current,
      moduleId,
      action,
      target,
      time: clock(),
      docId: local?.docId ?? (onCloud ? null : current.primary),
      line: local?.line ?? null,
      quality: local?.quality ?? (onCloud ? "none" : "fallback"),
      basis: local?.basis ?? "",
    };
    setSteps((previous) => [...previous, step]);
    setActiveStepId(step.id);
    if (local?.docId) setActiveDocId(local.docId);

    if (onCloud) {
      const stepId = step.id;
      void resolveCloud(moduleId, target, anchor)
        .then((resolved) => {
          setSteps((previous) =>
            previous.map((item) =>
              item.id === stepId
                ? { ...item, docId: resolved.docId, line: resolved.line, quality: resolved.quality, basis: resolved.basis }
                : item,
            ),
          );
        })
        .catch(() => undefined);
    }
  };

  const attachFrame = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc || doc === document) return;

    doc.addEventListener(
      "click",
      (event) => {
        const target = event.target as HTMLElement | null;
        if (!target) return;
        const node = target.closest(
          "button, a, [role='button'], .ant-btn, .ant-menu-item, li, td, th",
        ) as HTMLElement | null;
        const label = (node?.innerText || target.innerText || "").replace(/\s+/g, " ").trim().slice(0, 24);
        if (label.length < 2) return;
        pushStep(moduleRef.current, "点击", label, headerOf(target));
      },
      true,
    );

    const path = doc.location?.pathname ?? "";
    if (path && path !== lastPath.current) {
      lastPath.current = path;
      setPathLabel(path);
      pushStep(moduleRef.current, "进入页面", path);
    }
  };

  // 记录子应用内部的路由变化（同源时可读）
  useEffect(() => {
    const timer = window.setInterval(() => {
      const doc = frameRef.current?.contentDocument;
      const path = doc?.location?.pathname;
      if (!path) return;
      const full = path + (doc.location.hash || "");
      if (full !== lastPath.current) {
        lastPath.current = full;
        setPathLabel(full);
        pushStep(moduleRef.current, "进入页面", full);
      }
    }, 1500);
    return () => window.clearInterval(timer);
  }, []);

  const switchModule = (id: string) => {
    if (id === activeModule) return;
    setActiveModule(id);
    setActiveDocId(null);
    setPathLabel("");
    lastPath.current = "";
    setFrameKey((value) => value + 1);
    pushStep(id, "切换模块", findModule(id).label);
  };

  const openStep = (step: MirrorStep) => {
    setActiveStepId(step.id);
    if (step.moduleId !== activeModule) {
      setActiveModule(step.moduleId);
      setActiveDocId(step.docId);
      setFrameKey((value) => value + 1);
      return;
    }
    setActiveDocId(step.docId);
  };

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (loggingIn) return;
    setLoginError("");
    setLoggingIn(true);
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(login),
      });
      const data = (await response.json().catch(() => ({}))) as {
        lingpai?: string;
        yonghu?: { xingming?: string; zhanghao?: string };
        message?: string;
        statusMessage?: string;
      };
      if (!response.ok || !data.lingpai) {
        setLoginError(data.message || data.statusMessage || "登录失败，请检查账号密码");
        return;
      }
      sessionStorage.setItem(TOKEN_KEY, data.lingpai);
      sessionStorage.setItem("jhzz_xingming", data.yonghu?.xingming ?? data.yonghu?.zhanghao ?? "");
      setToken(data.lingpai);
      setUserName(data.yonghu?.xingming ?? data.yonghu?.zhanghao ?? "");
      setFrameKey((value) => value + 1);
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "网络异常");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem("jhzz_xingming");
    for (const key of ["gongzibiao_lingpai", "kaoqin_lingpai", "baobiao_lingpai", "cangcu_lingpai"]) {
      localStorage.removeItem(key);
    }
    setToken("");
    setUserName("");
    setFrameKey((value) => value + 1);
  };

  const switchMode = (mode: "local" | "cloud") => {
    setSourceMode(mode);
    localStorage.setItem("jhzz_source_mode", mode);
  };

  const submitCloudLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (cloudBusy) return;
    setCloudError("");
    setCloudBusy(true);
    try {
      saveCloudBase(cloudBase);
      const data = await cloudLogin(cloudPassword);
      sessionStorage.setItem("jhzz_code_root", data.root);
      setCloudRoot(data.root);
      setCloudPassword("");
    } catch (error) {
      setCloudError(error instanceof Error ? error.message : "连接失败");
    } finally {
      setCloudBusy(false);
    }
  };

  const disconnectCloud = () => {
    clearCloudToken();
    sessionStorage.removeItem("jhzz_code_root");
    setCloudRoot("");
  };

  const isMain = mod.id === "main";

  return (
    <div className="flex h-screen min-h-0 flex-col bg-[#0c131b]">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-[#16222e] bg-[#0a1118] px-5">
        <span className="flex h-7 w-7 items-center justify-center rounded-[6px] bg-gradient-to-br from-[#5fd0c2] to-[#4f8cff] text-[13px] font-bold text-[#08131a]">
          ◧
        </span>
        <span className="text-[13px] font-medium tracking-wide text-[#e7eef6]">君和展足 · 内测镜像台</span>
        <span className="h-4 w-px bg-[#22323f]" />
        <p className="truncate text-[11.5px] text-[#7c93a8]">左侧是内测系统的真实操作，右侧是这套系统对应的源码</p>
        <div className="ml-auto flex shrink-0 items-center gap-3 text-[11px] text-[#89a0b4]">
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#37d6a0]" />
            已记录 {steps.length} 步
          </span>
          {token ? (
            <button
              type="button"
              onClick={logout}
              className="rounded border border-[#22323f] px-2 py-1 text-[#9db3c7] transition-colors hover:border-[#3a5a74] hover:text-[#cfe3f5]"
            >
              {userName || "已登录"} · 退出
            </button>
          ) : null}
          <a
            href="https://nc.jhzzbgxt.cn/"
            target="_blank"
            rel="noreferrer"
            className="rounded border border-[#22323f] px-2 py-1 text-[#9db3c7] transition-colors hover:border-[#3a5a74] hover:text-[#cfe3f5]"
          >
            打开内测站
          </a>
        </div>
      </header>

      <div ref={shell} className={`flex min-h-0 flex-1 ${resizing ? "select-none" : ""}`}>
        <nav className="flex w-[88px] shrink-0 flex-col gap-1 border-r border-[#16222e] bg-[#0a1118] px-2 py-3">
          {SYSTEM_MODULES.map((item) => {
            const active = item.id === activeModule;
            return (
              <button
                key={item.id}
                type="button"
                title={item.hint}
                onClick={() => switchModule(item.id)}
                className={`flex flex-col items-center gap-1 rounded-md px-1 py-2.5 text-[11.5px] transition-colors ${
                  active
                    ? "bg-[#16283a] text-[#cfe3f5] ring-1 ring-[#27455f]"
                    : "text-[#728a9f] hover:bg-[#101b25] hover:text-[#a9bdcd]"
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-[#37d6a0]" : "bg-[#2b3d4d]"}`} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="flex min-w-0 flex-col" style={{ width: `${ratio * 100}%` }}>
          <div className="flex h-10 shrink-0 items-center gap-2 border-b border-[#e3ded2] bg-[#f7f5f0] px-3">
            <span className="shrink-0 text-[12px] font-medium text-[#1f2328]">{mod.label}</span>
            <span className="min-w-0 truncate rounded bg-white px-2 py-1 font-mono text-[10.5px] text-[#8d8679] ring-1 ring-[#e6e1d5]">
              {isMain ? "https://nc.jhzzbgxt.cn/" : pathLabel || moduleUrl(mod, token, sameOrigin)}
            </span>
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => setFrameKey((value) => value + 1)}
                className="rounded border border-[#ddd7c9] bg-white px-2 py-1 text-[11px] text-[#5d6672] transition-colors hover:border-[#c9c2b3]"
              >
                刷新
              </button>
              <a
                href={moduleUrl(mod, token, sameOrigin)}
                target="_blank"
                rel="noreferrer"
                className="rounded border border-[#ddd7c9] bg-white px-2 py-1 text-[11px] text-[#5d6672] transition-colors hover:border-[#c9c2b3]"
              >
                新窗口
              </a>
              <button
                type="button"
                onClick={() => setRecording((value) => !value)}
                className={`rounded border px-2 py-1 text-[11px] transition-colors ${
                  recording
                    ? "border-[#cfe3d6] bg-[#eef8f2] text-[#2f8b60]"
                    : "border-[#ddd7c9] bg-white text-[#9a9388]"
                }`}
              >
                {recording ? "记录中" : "已暂停"}
              </button>
            </div>
          </div>

          <div className="relative min-h-0 flex-1 bg-[#e9e6df]">
            {!isMain && !token ? (
              <div className="flex h-full items-center justify-center px-8">
                <form
                  onSubmit={submitLogin}
                  className="w-full max-w-[340px] rounded-lg border border-[#e0dbcf] bg-white p-6 shadow-[0_12px_40px_-24px_rgba(31,35,40,0.5)]"
                >
                  <p className="text-[14px] font-medium text-[#1f2328]">登录内测系统</p>
                  <p className="mt-1 text-[11.5px] leading-5 text-[#8d8679]">
                    子应用不单独登录，凭证由主系统统一签发。在这里登录后，四个子应用会直接带着登录态打开。
                  </p>
                  <label className="mt-4 block text-[11px] text-[#8d8679]">
                    账号
                    <input
                      value={login.zhanghao}
                      onChange={(event) => setLogin({ ...login, zhanghao: event.target.value })}
                      autoComplete="username"
                      className="mt-1 h-9 w-full rounded border border-[#e0dbcf] px-2.5 text-[13px] text-[#1f2328] outline-none focus:border-[#4f8cff]"
                    />
                  </label>
                  <label className="mt-3 block text-[11px] text-[#8d8679]">
                    密码
                    <input
                      type="password"
                      value={login.mima}
                      onChange={(event) => setLogin({ ...login, mima: event.target.value })}
                      autoComplete="current-password"
                      className="mt-1 h-9 w-full rounded border border-[#e0dbcf] px-2.5 text-[13px] text-[#1f2328] outline-none focus:border-[#4f8cff]"
                    />
                  </label>
                  {loginError ? <p className="mt-2 text-[11px] text-[#c0503a]">{loginError}</p> : null}
                  <button
                    type="submit"
                    disabled={loggingIn}
                    className="mt-4 h-9 w-full rounded bg-[#1f2328] text-[12.5px] text-white transition-colors hover:bg-[#333b45] disabled:opacity-60"
                  >
                    {loggingIn ? "登录中…" : "登录并进入"}
                  </button>
                  <p className="mt-3 text-[10.5px] leading-5 text-[#a8a094]">
                    登录请求由本地开发服务器转发到内测接口，账号密码只用于这次请求，不写入代码。
                  </p>
                </form>
              </div>
            ) : isMain ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
                <p className="text-[13px] font-medium text-[#3f4a57]">内测主站启用了访问密码</p>
                <p className="max-w-[440px] text-[11.5px] leading-6 text-[#8d8679]">
                  主应用入口返回 401（HTTP Basic 认证），iframe 会弹出系统密码框，部分浏览器会直接拒绝嵌入。工资表、考勤、报表、仓储四个子应用不受限制，可以直接在这里操作。
                </p>
                <a
                  href="https://nc.jhzzbgxt.cn/"
                  target="_blank"
                  rel="noreferrer"
                  className="rounded border border-[#c9c2b3] bg-white px-3 py-1.5 text-[12px] text-[#3f4a57] transition-colors hover:border-[#8d8679]"
                >
                  在新窗口打开内测主站
                </a>
                <p className="text-[11px] text-[#a8a094]">右侧仍展示主应用的 App / Buju / HomePage / api 源码</p>
              </div>
            ) : (
              <iframe
                key={`${mod.id}-${frameKey}`}
                ref={frameRef}
                src={moduleUrl(mod, token, sameOrigin)}
                title={mod.label}
                onLoad={() => {
                  window.setTimeout(attachFrame, 400);
                }}
                className="h-full w-full border-0 bg-white"
              />
            )}
          </div>

          <div className="shrink-0 border-t border-[#e3ded2] bg-[#fbfaf7]">
            <div className="flex items-center justify-between px-4 pb-1 pt-2 text-[11px] text-[#8d8679]">
              <span>操作时间轴 · {steps.length} 步</span>
              <span className="hidden sm:block">点击任一步：右侧定位到那一步的源码</span>
            </div>
            <div className="flex gap-2 overflow-x-auto px-4 py-2">
              {steps.length === 0 ? (
                <span className="text-[11px] text-[#a8a094]">在左侧点几下按钮，这里会出现操作记录</span>
              ) : null}
              {steps.map((step) => {
                const active = step.id === activeStepId;
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => openStep(step)}
                    className={`flex h-11 w-[138px] shrink-0 flex-col justify-center rounded border px-2 text-left transition-colors ${
                      active
                        ? "border-[#e2b768] bg-[#fdf3df] text-[#7a5410]"
                        : "border-[#e2ded4] bg-white text-[#5d6672] hover:border-[#c9c2b3]"
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-[11px] font-medium">
                      <span
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                          step.quality === "exact"
                            ? "bg-[#2fae7d]"
                            : step.quality === "header"
                              ? "bg-[#d4a24a]"
                              : "bg-[#c9c2b3]"
                        }`}
                      />
                      <span className="truncate">{step.target}</span>
                      <span className={`ml-auto font-mono text-[10px] ${active ? "text-[#a98544]" : "text-[#b3ab9d]"}`}>
                        {step.time.slice(0, 5)}
                      </span>
                    </span>
                    <span className={`mt-0.5 truncate font-mono text-[10px] ${active ? "text-[#9a7638]" : "text-[#a8a094]"}`}>
                      #{String(step.id).padStart(2, "0")} {step.action}
                      {step.line ? ` · L${step.line}` : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div
          role="separator"
          aria-orientation="vertical"
          onPointerDown={(event) => {
            setResizing(true);
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (!resizing || !shell.current) return;
            const rect = shell.current.getBoundingClientRect();
            const next = (event.clientX - rect.left) / rect.width;
            setRatio(Math.min(0.72, Math.max(0.36, next)));
          }}
          onPointerUp={(event) => {
            setResizing(false);
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          className="group relative w-1.5 shrink-0 cursor-col-resize bg-[#16222e]"
        >
          <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-[#22323f] transition-colors group-hover:bg-[#4f8cff]" />
        </div>

        <section className="flex min-w-0 flex-1 flex-col bg-[#0c131b] text-[#cfdae6]">
          <div className="flex shrink-0 items-center gap-2 border-b border-[#16222e] bg-[#0a1118] px-4 py-1.5 text-[11px] text-[#6e8599]">
            <span className="shrink-0">源码来源</span>
            {(["local", "cloud"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => switchMode(mode)}
                className={`shrink-0 rounded px-2 py-0.5 transition-colors ${
                  sourceMode === mode
                    ? "bg-[#17242f] text-[#cfe3f5] ring-1 ring-[#27455f]"
                    : "text-[#69809a] hover:bg-[#121c26] hover:text-[#a9bdcd]"
                }`}
              >
                {mode === "local" ? "本地副本" : "云端仓库"}
              </button>
            ))}
            <span className="ml-auto min-w-0 truncate font-mono text-[10.5px] text-[#4e6478]">
              {sourceMode === "cloud"
                ? cloudRoot
                  ? cloudRoot.split(/[\\/]/).filter(Boolean).slice(-2).join("/")
                  : "未连接"
                : `${mod.sources.length} 个文件`}
            </span>
            {sourceMode === "cloud" && cloudRoot ? (
              <button type="button" onClick={disconnectCloud} className="shrink-0 text-[#7c93a8] hover:text-[#cfe3f5]">
                断开
              </button>
            ) : null}
          </div>
          {sourceMode === "local" ? (
            <>
          <div className="flex shrink-0 items-start gap-3 border-b border-[#16222e] px-5 pb-3 pt-3.5">
            <span className="mt-0.5 rounded bg-[#16303f] px-2 py-1 font-mono text-[11px] text-[#5fd0c2]">{mod.label}</span>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-[14px] font-medium text-[#e7eef6]">
                {activeStep ? `${activeStep.action}「${activeStep.target}」` : "该模块的完整源码"}
              </h2>
              <p className="mt-0.5 truncate font-mono text-[11.5px] text-[#7c93a8]">
                {activeStep
                  ? activeStep.action === "点击"
                    ? activeStep.line && hitDoc
                      ? `【${qualityLabel(activeStep.quality)}】${hitDoc.doc.path} 第 ${activeStep.line} 行 · ${activeStep.basis}`
                      : `【未找到】${activeStep.basis || "没找到对应代码，下面是该模块主文件"}`
                    : `${activeStep.action} · 右侧列出 ${mod.label} 的 ${mod.sources.length} 个源文件`
                  : `${mod.sources.length} 个文件 · 主文件 ${docs[0]?.doc.path ?? mod.sources[0].path}`}
              </p>
            </div>
            <span className="shrink-0 rounded border border-[#24384a] px-2 py-1 text-[11px] text-[#8fa8bd]">
              {activeDoc ? `${activeDoc.code.split("\n").length} 行` : docsLoading ? "读取中" : "0 行"}
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-[#16222e] bg-[#0a1118] px-3 py-1.5">
            {docsLoading && docs.length === 0 ? (
              <span className="px-2 py-1 font-mono text-[11px] text-[#4e6478]">读取源码…</span>
            ) : null}
            {docs.map(({ doc: item }) => {
              const active = item.id === activeDoc?.doc.id;
              const marked = item.id === activeStep?.docId && activeStep.line;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveDocId(item.id)}
                  className={`flex shrink-0 items-center gap-1.5 rounded px-2 py-1 font-mono text-[11px] transition-colors ${
                    active ? "bg-[#17242f] text-[#cfe3f5]" : "text-[#69809a] hover:bg-[#121c26]"
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${marked ? "bg-[#e2b768]" : "bg-[#2b4a3f]"}`} />
                  {item.label}
                </button>
              );
            })}
            <span className="ml-auto hidden pr-2 text-[10.5px] text-[#4e6478] xl:block">{activeDoc?.doc.path ?? ""}</span>
          </div>

          <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
            <div className="overflow-hidden rounded border border-[#1a2836]">
              <div className="flex items-center gap-2 border-b border-[#1a2836] bg-[#101b25] px-3 py-1.5">
                <span className="font-mono text-[11px] text-[#7fb3ff]">{activeDoc?.doc.path ?? ""}</span>
                <span className="ml-auto font-mono text-[10.5px] text-[#4e6478]">
                  {activeStep?.moduleId === mod.id && activeDoc && activeStep.docId === activeDoc.doc.id && activeStep.line
                    ? `第 ${activeStep.line} 行`
                    : "完整文件"}
                </span>
              </div>
              {activeDoc ? (
                <SyntaxCode code={activeDoc.code} highlightRange={highlight} />
              ) : (
                <p className="px-3 py-6 font-mono text-[11.5px] text-[#4e6478]">读取源码…</p>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3 border-t border-[#16222e] px-4 py-2.5">
            <p className="min-w-0 flex-1 truncate text-[11px] text-[#6e8599]">
              {mod.hint} · 源码取自本机保存的前端工程副本，可能与线上版本有差异
            </p>
            <a
              href={moduleUrl(mod, token, sameOrigin)}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 rounded border border-[#24384a] px-2.5 py-1 text-[11px] text-[#9db3c7] transition-colors hover:border-[#3a5a74] hover:text-[#cfe3f5]"
            >
              单独打开
            </a>
          </div>
            </>
          ) : cloudRoot ? (
            <CloudPanel
              activePath={activeStep?.moduleId === mod.id ? activeStep.docId : null}
              activeLine={activeStep?.moduleId === mod.id ? activeStep.line : null}
              activeStepId={activeStep?.id ?? null}
              hint={mod.id}
              basis={activeStep?.moduleId === mod.id ? activeStep.basis : ""}
              quality={activeStep?.moduleId === mod.id ? activeStep.quality : undefined}
            />
          ) : (
            <div className="flex min-h-0 flex-1 items-center justify-center px-8">
              <form
                onSubmit={submitCloudLogin}
                className="w-full max-w-[340px] rounded-lg border border-[#24384a] bg-[#0e1822] p-6"
              >
                <p className="text-[13px] font-medium text-[#e7eef6]">连接云端代码仓库</p>
                <p className="mt-1 text-[11.5px] leading-5 text-[#7c93a8]">
                  填云电脑上代码服务的地址与口令。nginx 反代好了就留默认的 <span className="font-mono text-[#9db3c7]">/api/code</span>；
                  没配 nginx 就填 <span className="font-mono text-[#9db3c7]">http://云电脑IP:6390</span>，走直连。
                </p>
                <input
                  value={cloudBase}
                  onChange={(event) => setCloudBase(event.target.value)}
                  placeholder="/api/code 或 http://云电脑IP:6390"
                  className="mt-3 h-9 w-full rounded border border-[#24384a] bg-[#0a1118] px-2.5 font-mono text-[11.5px] text-[#cfe3f5] outline-none placeholder:text-[#4e6478] focus:border-[#3a5a74]"
                />
                <input
                  type="password"
                  value={cloudPassword}
                  onChange={(event) => setCloudPassword(event.target.value)}
                  placeholder="代码服务口令"
                  className="mt-2 h-9 w-full rounded border border-[#24384a] bg-[#0a1118] px-2.5 text-[12.5px] text-[#e7eef6] outline-none placeholder:text-[#4e6478] focus:border-[#3a5a74]"
                />
                {cloudError ? <p className="mt-2 text-[11px] leading-5 text-[#e08272]">{cloudError}</p> : null}
                <button
                  type="submit"
                  disabled={cloudBusy}
                  className="mt-3 h-9 w-full rounded bg-[#1f6f5c] text-[12.5px] text-white transition-colors hover:bg-[#268a72] disabled:opacity-60"
                >
                  {cloudBusy ? "连接中…" : "连接"}
                </button>
                <p className="mt-3 text-[10.5px] leading-5 text-[#4e6478]">
                  口令只在这次请求里使用，浏览器仅保存服务端签发的一次性令牌。
                </p>
              </form>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
